import {
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import { memo, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { ResolutionFactoCohabitationData as ResolutionCohabitationDataVM } from 'mutation/view-models/resolution-facto-cohabitation';
import { FormikErrors, FormikTouched } from 'formik';
import MunicipalityList from 'shell/components/shared/municipality-list';
import { PmiInputFile } from 'shell/components/shared/file-input';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { readFileAsDataUrlBase64 } from 'shell/utils/file-to-base64';
import ProfessionistList from 'shell/components/shared/professionist-select';
import FormForeignEventMemo, {
  ForeignMunicipality,
} from 'shell/components/shared/event-form/form-foreign-event';
import FormItalyEventMemo, {
  ItalianMunicipality,
} from 'shell/components/shared/event-form/form-italy-event';
import TypeVoidCivilUnionList from 'shell/components/shared/type-void-civil-union-list';

interface ResolutionCohabitationDataProps {
  values?: ResolutionCohabitationDataVM;
  onChange: (
    key: keyof ResolutionCohabitationDataVM,
    value: ResolutionCohabitationDataVM[keyof ResolutionCohabitationDataVM]
  ) => void;
  onChangeValues: (value: ResolutionCohabitationDataVM) => void;
  touched?: FormikTouched<ResolutionCohabitationDataVM>;
  errors?: FormikErrors<ResolutionCohabitationDataVM>;
}

const ResolutionCohabitationDataComponent = ({
  values,
  onChange,
  onChangeValues,
  errors,
  touched,
}: ResolutionCohabitationDataProps): React.ReactElement => {
  const handleAddFile = useCallback(
    (file) => {
      readFileAsDataUrlBase64(file).then((newFile) => {
        onChange('file', newFile);
      });
    },
    [onChange]
  );

  const onChangeRadio = useEventCallback(() => {
    onChangeValues({
      eventItaly: null,
      eventForeign: null,
      happenInItaly: !values?.happenInItaly,
    });
  });

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={4}>
        <TypeVoidCivilUnionList
          label="Motivo risoluzione"
          aria-label="Motivo risoluzione"
          id="reason-termination"
          required
          value={values?.reasonTermination?.toString() || ''}
          onChange={(value) => {
            onChange('reasonTermination', value);
          }}
          error={touched?.reasonTermination && Boolean(errors?.reasonTermination)}
          filterList={(items) =>
            items.filter((item) => ['31', '32', '34', '98'].includes(String(item.id)))
          }
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Evento"
              id="happeInItaly"
              value={values?.happenInItaly || 'false'}
              onChange={() => {
                onChangeRadio();
              }}
            >
              <FormControlLabel value="false" control={<Radio />} label="Evento Italia" />
              <FormControlLabel value="true" control={<Radio />} label="Evento Stato estero" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} container spacing={0} columnSpacing={{ xs: 0, md: 1 }}>
          {!values?.happenInItaly ? (
            <FormItalyEventMemo
              form={values?.eventItaly}
              handleChange={(eventItaly) => onChange('eventItaly', eventItaly)}
              errors={errors?.eventItaly as FormikErrors<ItalianMunicipality>}
              touched={touched?.eventItaly as unknown as FormikTouched<ItalianMunicipality>}
            />
          ) : (
            <FormForeignEventMemo
              form={values?.eventForeign}
              handleChange={(eventForeign) => onChange('eventForeign', eventForeign)}
              errors={errors?.eventForeign as FormikErrors<ForeignMunicipality>}
              touched={touched?.eventForeign as unknown as FormikTouched<ForeignMunicipality>}
            />
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Dati risoluzione convivenza</PmiTitleDivider>
      </Grid>
      <Grid container item xs={12} spacing={0} rowSpacing={{ xs: 1, md: 0 }} columnSpacing={1}>
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Data cessazione convivenza *"
            aria-label="Data cessazione convivenza *"
            value={values?.endCohabitationDate || null}
            onChange={(date) => {
              onChange('endCohabitationDate', date);
            }}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="endCohabitationDate"
                fullWidth
                error={touched?.endCohabitationDate && Boolean(errors?.endCohabitationDate)}
                helperText={errors?.endCohabitationDate}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container item xs={12} spacing={0} rowSpacing={{ xs: 1, md: 0 }} columnSpacing={1}>
        <Grid item xs={12} md={4}>
          <DatePicker
            label="Data notifica"
            aria-label="Data notifica"
            value={values?.dateNotification || null}
            onChange={(date) => {
              onChange('dateNotification', date);
            }}
            renderInput={(params) => (
              <PmiTextField
                {...params}
                id="dateNotification"
                fullWidth
                error={touched?.dateNotification && Boolean(errors?.dateNotification)}
                helperText={errors?.dateNotification}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <PmiTextField
            label="Protocollo notifica al comune"
            aria-label="Protocollo notifica al comune"
            id="protocol"
            fullWidth
            value={values?.protocol || ''}
            onChange={(event) => {
              onChange('protocol', event.target.value);
            }}
            error={touched?.protocol && Boolean(errors?.protocol)}
            helperText={errors?.protocol}
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12} md={4}>
          <MunicipalityList
            value={values?.municipality || null}
            onChange={(municipality) => onChange('municipality', municipality)}
            label="Comune stipula risoluzione"
            id="municipality"
            fromDate={values?.endCohabitationDate}
            error={touched?.municipality && !!errors?.municipality}
            required
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiTextField
            label="Provincia"
            aria-label="Provincia"
            id="province"
            fullWidth
            value={values?.municipality?.province || ''}
            disabled
          />
        </Grid>
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12} md={4}>
          <ProfessionistList
            value={values?.professionist || ''}
            type={values?.list}
            onChange={(professionist) => onChange('professionist', professionist)}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        File contratto&nbsp;
        <PmiInputFile
          multiple={false}
          file={values?.file || undefined}
          onChange={handleAddFile}
          color="cdmBrandThird"
        />
      </Grid>
    </Grid>
  );
};

ResolutionCohabitationDataComponent.displayName = 'Form cohabitation resolution data component';

const ResolutionFactoCohabitationData = memo(ResolutionCohabitationDataComponent);

export default ResolutionFactoCohabitationData;
